import type { Editor as TiptapEditor } from "@tiptap/react"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

import type { QnrEdit } from "features/Assistant/types"
import { editorLines } from "./editorLines"

/** An edit's place in the document, in ProseMirror positions. */
interface PlacedEdit {
  index: number
  from: number
  to: number
}

const sameLines = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((line, i) => line === b[i])

/**
 * Applies the chosen questionnaire edits to the editor as ONE transaction, so
 * they undo in one step. Each edit replaces lines `fromLine..toLine` (1-based,
 * inclusive; `toLine = fromLine - 1` inserts before `fromLine`) with one
 * paragraph per entry of `newLines`.
 *
 * An edit is stale, and is not applied, when its lines in the editor no longer
 * equal its `oldLines` (the user changed them since the proposal), when its
 * range is outside the document, or when it overlaps an earlier edit in the
 * list that is applied. The other edits still apply.
 *
 * The change goes through `view.dispatch(tr.replaceWith(...))`, never
 * `setContent`, so the Yjs binding sees a normal edit in collaborative mode.
 *
 * @returns indexes into `edits` of the applied and the stale edits
 */
export const applyQnrEdits = (
  editor: TiptapEditor,
  edits: QnrEdit[],
): { applied: number[]; stale: number[] } => {
  const { state, view, schema } = editor
  const lines = editorLines(editor)

  // Start position of each top-level paragraph, plus the document end
  const starts: number[] = []
  let pos = 0
  state.doc.forEach((node) => {
    starts.push(pos)
    pos += node.nodeSize
  })
  starts.push(state.doc.content.size)

  const placed: PlacedEdit[] = []
  const stale: number[] = []
  edits.forEach((edit, index) => {
    const { fromLine, toLine, oldLines } = edit
    const inRange =
      Number.isInteger(fromLine) &&
      Number.isInteger(toLine) &&
      fromLine >= 1 &&
      toLine >= fromLine - 1 &&
      toLine <= lines.length
    if (!inRange || !sameLines(lines.slice(fromLine - 1, toLine), oldLines)) {
      stale.push(index)
      return
    }
    const from = starts[fromLine - 1]
    const to = starts[toLine]
    // Ranges may touch but not overlap; an insert may sit at either end of a range
    const overlaps = placed.some((p) => from < p.to && p.from < to)
    if (overlaps) {
      stale.push(index)
      return
    }
    placed.push({ index, from, to })
  })

  if (placed.length > 0) {
    // Bottom-up, so earlier positions stay valid. At the same start, the
    // replacement goes first so an insert there lands before its new lines;
    // inserts at the same place keep their list order.
    const order = [...placed].sort(
      (a, b) => b.from - a.from || b.to - a.to || b.index - a.index,
    )
    const tr = state.tr
    for (const { index, from, to } of order) {
      const paragraphs: ProseMirrorNode[] = edits[index].newLines.map((text) =>
        schema.nodes.paragraph.create(null, text ? schema.text(text) : null),
      )
      tr.replaceWith(from, to, paragraphs)
    }
    view.dispatch(tr)
  }

  return {
    applied: placed.map((p) => p.index).sort((a, b) => a - b),
    stale,
  }
}
